import React, { useState, useRef, useMemo } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useSpeciesField } from '../hooks/useSpeciesField';
import {
  Box,
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  useMediaQuery,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Select,
  useToast,
} from '@chakra-ui/react';

const CaseDetailsModal = ({ isOpen, onClose, selectedCase, onEdit, onSaveSuccess, userRole }) => {
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isEditInvestigation, setIsEditInvestigation] = useState(false);
  const [isEditRenestPlan, setIsEditRenestPlan] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const toast = useToast();
  
  // Get species field utilities from hook
  const { speciesOptions, getSpeciesByCode, getSpeciesDisplay, SPECIES_LIST } = useSpeciesField();
  
  const isAdmin = userRole === 'admin';
  const isVolunteer = userRole === 'volunteer';
  const canEditInvestigation = isAdmin || isVolunteer;
  const canEditRenestPlan = isAdmin || isVolunteer;
  
  // Convert date from MM/DD/YY to YYYY-MM-DD for form
  const getFormattedDate = (dateStr) => {
    if (!dateStr || !dateStr.includes('/')) return dateStr;
    try {
      const [month, day, year] = dateStr.split('/');
      const fullYear = year.length === 2 ? (parseInt(year) < 50 ? '20' + year : '19' + year) : year;
      return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    } catch (e) {
      return dateStr;
    }
  };

  // Prepare default values with converted date
  const defaultFormValues = React.useMemo(() => {
    if (!selectedCase) return {};
    return {
      ...selectedCase,
      date: getFormattedDate(selectedCase.date),
    };
  }, [selectedCase?.id, selectedCase?.date]);

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: defaultFormValues,
  });

  // Watch for selectedCase changes to reset form
  const prevCaseRef = useRef(selectedCase?.id);
  React.useEffect(() => {
    if (selectedCase?.id !== prevCaseRef.current) {
      reset(defaultFormValues);
      prevCaseRef.current = selectedCase?.id;
    }
  }, [defaultFormValues, reset, selectedCase?.id]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'completed':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const onSave = async (formData) => {
    // Validate required fields when in edit mode (only for patient section)
    if (isEditMode) {
      const errors = {};
      if (!formData.title || formData.title.trim() === '') {
        errors.title = 'Species is required';
      }
      if (!formData.location || formData.location.trim() === '') {
        errors.location = 'General Region is required';
      }

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      // Clear validation errors if all fields are valid
      setValidationErrors({});
    }

    try {
      const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
      
      const dataToSave = { ...formData };
      
      // Convert date back to MM/DD/YY format for backend
      if (dataToSave.date && dataToSave.date.includes('-')) {
        const [year, month, day] = dataToSave.date.split('-');
        const yearShort = year.slice(-2);
        dataToSave.date = `${month}/${day}/${yearShort}`;
      }
      
      const response = await fetch(`${baseApiUrl}/cases.php`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: selectedCase.id,
          title: dataToSave.title,
          date: dataToSave.date,
          location: dataToSave.location,
          description: dataToSave.description,
          pointOfContact: dataToSave.pointOfContact,
          status: dataToSave.status,
          investigationStatus: dataToSave.investigationStatus,
          investigationNotes: dataToSave.investigationNotes,
          conclusion: dataToSave.conclusion,
          rrPlan: dataToSave.rrPlan,
          caseSummary: dataToSave.caseSummary,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update case: ${response.status}`);
      }

      const result = await response.json();
      console.log('Case updated successfully:', result);
      
      toast({
        title: 'Success',
        description: 'Case updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      setIsEditMode(false);
      setIsEditInvestigation(false);
      setIsEditRenestPlan(false);
      
      // Call the success callback to refresh the cases list
      if (onSaveSuccess) {
        await onSaveSuccess();
      }
    } catch (error) {
      console.error('Error saving case:', error);
      alert('Failed to save case. Please try again.');
    }
  };

  const handleCancel = () => {
    reset(selectedCase || {});
    setIsEditMode(false);
    setValidationErrors({});
  };

  const handleCloseModal = () => {
    setIsEditMode(false);
    setIsEditInvestigation(false);
    setIsEditRenestPlan(false);
    setValidationErrors({});
    onClose();
  };

  const PatientInfoField = ({ label, field, isReadOnly = false, section = 'patient' }) => {
    console.log(`PatientInfoField rendering: ${field}`);
    
    // Determine which edit mode to use based on section
    const shouldEdit = section === 'investigation' ? isEditInvestigation : section === 'renestPlan' ? isEditRenestPlan : isEditMode;
    
    if (shouldEdit && !isReadOnly) {
      // Use select dropdown for species field
      if (field === 'title') {
        return (
          <Controller
            name={field}
            control={control}
            render={({ field: fieldProps }) => {
              const currentValue = fieldProps.value || '';
              const matchedSpecies = SPECIES_LIST.find(s => s.code === currentValue);
              const currentCode = matchedSpecies ? matchedSpecies.code : '';

              return (
                <FormControl isInvalid={!!validationErrors.title}>
                  <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                    {label}
                  </FormLabel>
                  <Select
                    {...fieldProps}
                    value={currentCode}
                    onChange={(e) => {
                      console.log(`Species Select onChange fired, re-rendering species options`);
                      const selectedSpecies = SPECIES_LIST.find(s => s.code === e.target.value);
                      fieldProps.onChange(selectedSpecies ? selectedSpecies.code : '');
                    }}
                    size="sm"
                    borderColor={validationErrors.title ? 'red.500' : undefined}
                  >
                    <option value="">-- Select Species --</option>
                    {speciesOptions}
                  </Select>
                  {validationErrors.title && (
                    <Text fontSize="sm" color="red.500" mt={1}>
                      {validationErrors.title}
                    </Text>
                  )}
                </FormControl>
              );
            }}
          />
        );
      }

      // Use date picker for date field
      if (field === 'date') {
        return (
          <Controller
            name={field}
            control={control}
            render={({ field: fieldProps }) => (
              <FormControl>
                <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                  {label}
                </FormLabel>
                <Input
                  type="date"
                  {...fieldProps}
                  size="sm"
                />
              </FormControl>
            )}
          />
        );
      }

      // Use dropdown for movements field
      if (field === 'description') {
        const movementOptions = ['Keep at Bahde', 'Keep at Ramona', 'Move through care'];
        return (
          <Controller
            name={field}
            control={control}
            render={({ field: fieldProps }) => (
              <FormControl>
                <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                  {label}
                </FormLabel>
                <Select
                  {...fieldProps}
                  value={fieldProps.value || ''}
                  size="sm"
                >
                  <option value="">-- Select Movement --</option>
                  {movementOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        );
      }

      // Use dropdown for status field
      if (field === 'status') {
        const statusOptions = [
          'Candidate',
          'Candidate: Wild Foster',
          'Investigation',
          'R&R in Process',
          'Renest Successful',
          'Foster Successful',
          'Renest/Foster Unsuccessful',
          'Patient HE/DIC',
          'Not a Candidate',
        ];
        return (
          <Controller
            name={field}
            control={control}
            render={({ field: fieldProps }) => (
              <FormControl>
                <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                  {label}
                </FormLabel>
                <Select
                  {...fieldProps}
                  value={fieldProps.value || ''}
                  size="sm"
                >
                  <option value="">-- Select Status --</option>
                  {statusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        );
      }

      // Use dropdown for investigation status field
      if (field === 'investigationStatus') {
        const statusOptions = ['In Process', 'Complete'];
        return (
          <Controller
            name={field}
            control={control}
            render={({ field: fieldProps }) => (
              <FormControl>
                <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                  {label}
                </FormLabel>
                <Select
                  {...fieldProps}
                  value={fieldProps.value || ''}
                  size="sm"
                >
                  <option value="">-- Select Status --</option>
                  {statusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        );
      }

      // Use textarea for investigation notes field
      if (field === 'investigationNotes') {
        return (
          <Controller
            name={field}
            control={control}
            render={({ field: fieldProps }) => (
              <FormControl>
                <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                  {label}
                </FormLabel>
                <Textarea
                  {...fieldProps}
                  size="sm"
                  minH="100px"
                />
              </FormControl>
            )}
          />
        );
      }

      // Use dropdown for conclusion field
      if (field === 'conclusion') {
        const conclusionOptions = [
          'Nest/Area Acceptable',
          'Nest/Area Not Acceptable',
          'Did not find nest',
          'Can\'t Access nest/Area not safe',
          'Not Enough Information/Could Not Contact Finder',
          'No Permission from Property Owner',
        ];
        return (
          <Controller
            name={field}
            control={control}
            render={({ field: fieldProps }) => (
              <FormControl>
                <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                  {label}
                </FormLabel>
                <Select
                  {...fieldProps}
                  value={fieldProps.value || ''}
                  size="sm"
                >
                  <option value="">-- Select Conclusion --</option>
                  {conclusionOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        );
      }

      // Use textarea for rrPlan and caseSummary fields
      if (field === 'rrPlan' || field === 'caseSummary') {
        return (
          <Controller
            name={field}
            control={control}
            render={({ field: fieldProps }) => (
              <FormControl>
                <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                  {label}
                </FormLabel>
                <Textarea
                  {...fieldProps}
                  size="sm"
                  minH="100px"
                />
              </FormControl>
            )}
          />
        );
      }

      return (
        <Controller
          name={field}
          control={control}
          render={({ field: fieldProps }) => (
            <FormControl isInvalid={!!validationErrors[field]}>
              <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                {label}
              </FormLabel>
              <Input
                {...fieldProps}
                size="sm"
                borderColor={validationErrors[field] ? 'red.500' : undefined}
              />
              {validationErrors[field] && (
                <Text fontSize="sm" color="red.500" mt={1}>
                  {validationErrors[field]}
                </Text>
              )}
            </FormControl>
          )}
        />
      );
    }

    // Read-only mode - use Controller to get value without watch
    return (
      <Controller
        name={field}
        control={control}
        render={({ field: fieldProps }) => {
          const formValue = fieldProps.value;
          const displayValue = field === 'title' ? getSpeciesDisplay(formValue) : (formValue || 'N/A');

          return (
            <Box>
              <Text fontSize="xs" color="gray.700" fontWeight="semibold">
                {label}
              </Text>
              <Text fontSize="sm" whiteSpace="pre-wrap" color="gray.900">{displayValue}</Text>
            </Box>
          );
        }}
      />
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCloseModal} size={isMobile ? "full" : "2xl"} scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent maxH="100vh" borderRadius="2xl" boxShadow="0 20px 60px rgba(0, 0, 0, 0.2)">
        <ModalHeader fontSize="2xl" fontWeight="bold" bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" color="white" borderRadius="2xl 2xl 0 0">{selectedCase?.case_number || 'N/A'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {selectedCase && (
            <VStack spacing={6} align="stretch">
              {/* Patient Info Section */}
              <Box>
                <HStack justify="space-between" align="center" mb={3}>
                  <Box>
                    <Heading as="h3" size="md" color="blue.800">
                      Staff Only
                    </Heading>
                    <Heading as="h4" size="sm" color="gray.700" mt={1}>
                      Patient Info
                    </Heading>
                  </Box>
                  {isAdmin && (
                    <Button
                      size="sm"
                      bg={isEditMode ? 'gray.300' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}
                      color={isEditMode ? 'gray.700' : 'white'}
                      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                      fontWeight="600"
                      onClick={() => {
                        setIsEditMode(!isEditMode);
                        if (!isEditMode) {
                          setIsEditInvestigation(false);
                          setIsEditRenestPlan(false);
                        }
                      }}
                    >
                      {isEditMode ? 'Cancel Edit' : 'Edit'}
                    </Button>
                  )}
                </HStack>
                {isEditMode && (
                  <form onSubmit={handleSubmit(onSave)}>
                    <VStack spacing={3} align="stretch">
                      <PatientInfoField label="SPECIES" field="title" section="patient" />
                      <PatientInfoField label="Case Number" field="case_number" isReadOnly={true} section="patient" />
                      <PatientInfoField label="DATE ADMITTED" field="date" section="patient" />
                      <PatientInfoField label="GENERAL REGION/CITY" field="location" section="patient" />
                      <PatientInfoField label="POINT OF CONTACT (VOLUNTEER)" field="pointOfContact" section="patient" />
                      <PatientInfoField label="MOVEMENTS" field="description" section="patient" />
                      <PatientInfoField label="STATUS" field="status" section="patient" />
                    </VStack>

                    <HStack spacing={3} mt={4} justify="flex-end">
                      <Button size="sm" variant="outline" type="button" onClick={handleCancel} fontWeight="600">
                        Cancel
                      </Button>
                      <Button size="sm" bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" color="white" _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }} type="submit" fontWeight="600">
                        Save Changes
                      </Button>
                    </HStack>
                  </form>
                )}
                {!isEditMode && (
                  <VStack spacing={3} align="stretch">
                    <PatientInfoField label="SPECIES" field="title" section="patient" />
                    <PatientInfoField label="Case Number" field="case_number" isReadOnly={true} section="patient" />
                    <PatientInfoField label="DATE ADMITTED" field="date" section="patient" />
                    <PatientInfoField label="GENERAL REGION/CITY" field="location" section="patient" />
                    <PatientInfoField label="POINT OF CONTACT (VOLUNTEER)" field="pointOfContact" section="patient" />
                    <PatientInfoField label="MOVEMENTS" field="description" section="patient" />
                    <PatientInfoField label="STATUS" field="status" section="patient" />
                  </VStack>
                )}
              </Box>

              <Divider />

              {/* Nest Investigation Section */}
              <Box>
                <HStack justify="space-between" align="center" mb={3}>
                  <Heading as="h3" size="md" color="blue.800">
                    Nest Investigation (Volunteer)
                  </Heading>
                  {canEditInvestigation && (
                    <Button
                      size="sm"
                      bg={isEditInvestigation ? 'gray.300' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}
                      color={isEditInvestigation ? 'gray.700' : 'white'}
                      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                      fontWeight="600"
                      onClick={() => {
                        setIsEditInvestigation(!isEditInvestigation);
                        if (!isEditInvestigation) {
                          setIsEditMode(false);
                          setIsEditRenestPlan(false);
                        }
                      }}
                    >
                      {isEditInvestigation ? 'Cancel Edit' : 'Edit'}
                    </Button>
                  )}
                </HStack>
                {isEditInvestigation && (
                  <form onSubmit={handleSubmit(onSave)}>
                    <VStack spacing={3} align="stretch">
                      <PatientInfoField label="INVESTIGATION STATUS" field="investigationStatus" section="investigation" />
                      <PatientInfoField label="NOTES NEST INVESTIGATION FORM" field="investigationNotes" section="investigation" />
                      <PatientInfoField label="CONCLUSION" field="conclusion" section="investigation" />
                    </VStack>

                    <HStack spacing={3} mt={4} justify="flex-end">
                      <Button size="sm" variant="outline" type="button" onClick={() => setIsEditInvestigation(false)} fontWeight="600">
                        Cancel
                      </Button>
                      <Button size="sm" bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" color="white" _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }} type="submit" fontWeight="600">
                        Save Changes
                      </Button>
                    </HStack>
                  </form>
                )}
                {!isEditInvestigation && (
                  <VStack spacing={3} align="stretch">
                    <PatientInfoField label="INVESTIGATION STATUS" field="investigationStatus" section="investigation" />
                    <PatientInfoField label="NOTES NEST INVESTIGATION FORM" field="investigationNotes" section="investigation" />
                    <PatientInfoField label="CONCLUSION" field="conclusion" section="investigation" />
                  </VStack>
                )}
              </Box>

              <Divider />

              {/* Renest Plan Section */}
              <Box>
                <HStack justify="space-between" align="center" mb={3}>
                  <Heading as="h3" size="md" color="blue.800">
                    Renest Plan (Volunteer)
                  </Heading>
                  {canEditRenestPlan && (
                    <Button
                      size="sm"
                      bg={isEditRenestPlan ? 'gray.300' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}
                      color={isEditRenestPlan ? 'gray.700' : 'white'}
                      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                      fontWeight="600"
                      onClick={() => {
                        setIsEditRenestPlan(!isEditRenestPlan);
                        if (!isEditRenestPlan) {
                          setIsEditMode(false);
                          setIsEditInvestigation(false);
                        }
                      }}
                    >
                      {isEditRenestPlan ? 'Cancel Edit' : 'Edit'}
                    </Button>
                  )}
                </HStack>
                {isEditRenestPlan && (
                  <form onSubmit={handleSubmit(onSave)}>
                    <VStack spacing={3} align="stretch">
                      <PatientInfoField label="R&R PLAN" field="rrPlan" section="renestPlan" />
                      <PatientInfoField label="CASE SUMMARY" field="caseSummary" section="renestPlan" />
                    </VStack>

                    <HStack spacing={3} mt={4} justify="flex-end">
                      <Button size="sm" variant="outline" type="button" onClick={() => setIsEditRenestPlan(false)} fontWeight="600">
                        Cancel
                      </Button>
                      <Button size="sm" bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" color="white" _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }} type="submit" fontWeight="600">
                        Save Changes
                      </Button>
                    </HStack>
                  </form>
                )}
                {!isEditRenestPlan && (
                  <VStack spacing={3} align="stretch">
                    <PatientInfoField label="R&R PLAN" field="rrPlan" section="renestPlan" />
                    <PatientInfoField label="CASE SUMMARY" field="caseSummary" section="renestPlan" />
                  </VStack>
                )}
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="outline" onClick={handleCloseModal} fontWeight="600">
              Close
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CaseDetailsModal;
